import Connector from './Connection/PusherConnection';
import ParamsValidator from './utils/ParamsValidator';
import ParamsValidationError from './errors/ParamsValidationError';
import ChannelSubscriptionError from './errors/ChannelSubscriptionError';
import WSConnectionError from './errors/WSConnectionError';
import PrivateChannel from './Connection/Channels/PrivateChannel';
import PresenceChannel from './Connection/Channels/PresenceChannel';

global.pushkin;

class Pushkin {

    /**
     * @constructs [Pushkin]
     * @property {object} connection - Pusher connection
     */
    constructor(connection) {
        this.connection = connection;
    }

    /**
     * @returns {string} string represent connection state: 'initialized', 'connecting', 'connected', 'unavailable', 'failed', 'disconnected'
     */
    get connectionState() {
        return this.connection.state();
    }

    /**
     * @returns {object} Object contains all connection statuses constants
     */
    static get connectionStatus(){
        return Connector.connectionStatuses;
    }

    /**
     * @param {object} options
     * @property {string} options.appKey - RTE provider app key
     * @property {string} options.cluster - RTE provider cluster
     * @property {function} options.logger - Enable and get connection and channel logs
     * @param {string[]} channelNames - the list of channel names which will be preauthorized
     * @param {string[]} clientEventsChannelNames - the list of clientEvents channel names which will be preauthorized
     * @returns {Promise<Pushkin>} Promise which return <Pushkin> in resolve or (ParamsValidationError, WSConnectionError) error in reject
     */
    static connect(options, channelNames, clientEventsChannelNames) {
        if (global.pushkin) {
            return Promise.resolve(global.pushkin);
        }

        const validationErrorMessage = ParamsValidator.validate(options, 'connect');
        if (validationErrorMessage) {
            return Promise.reject(new ParamsValidationError(validationErrorMessage));
        }

        const { appKey, cluster, logger, debugMode } = options;
        return Connector.connect(
            appKey,
            cluster,
            logger,
            debugMode,
            channelNames,
            clientEventsChannelNames
        ).then((connection) => {
            global.pushkin = Object.freeze(new Pushkin(connection));
            return global.pushkin;
        });
    }

    /**
     * @param {function} callback - Callback function which will be triggered on connection state change with state object
     */
    connectionStateChange(callback) {
        const validationErrorMessage = ParamsValidator.validate({ callback }, 'state');

        if (validationErrorMessage) {
            return new ParamsValidationError(validationErrorMessage);
        }

        this.connection.stateChange(callback);
    }

    /**
     * @return {Void}
     */
    disconnect() {
        this.connection.disconnect();
    }

    /**
     * @param {Object} options
     * @property {string} options.channelName - name of communication channell
     * @returns {Promise<PrivateChannel>} channel promise
     */
    subscribe(options) {
        const validationError = ParamsValidator.validate(options, 'subscribe');
        if (validationError) {
            throw new ParamsValidationError(validationError);
        }

        return this.connection.subscribe(options.channelName, PrivateChannel);
    }

    /**
     * @param {Object} options
     * @property {string} options.channelName - name of a clientEvents channel
     * @returns {Promise<PresenceChannel>} clientEvents channel promise
     */
    subscribeToClientEventsChannel(options) {
        const validationError = ParamsValidator.validate(options, 'subscribeToClientEventsChannel');
        if (validationError) {
            throw new ParamsValidationError(validationError);
        }

        return this.connection.subscribe(options.channelName, PresenceChannel);
    }

    /**
     * @param {string} channelName - name of a communication channel
     */
    unsubscribe(channelName) {
        this.connection.unsubscribe(channelName, PrivateChannel);
    }

    /**
     * @param {string} channelName - name of a clientEvents channel
     */
    unsubscribeFromClientEventsChannel(channelName) {
        this.connection.unsubscribe(channelName, PresenceChannel);
    }

    /**
     * @param {string} channelName - name of a communication channel
     * @returns {PrivateChannel} channel instance
     */
    getChannel(channelName) {
        return this.connection.getChannel(channelName, PrivateChannel);
    }

    /**
     * @param {string} channelName - name of a clientEvents channel
     * @returns {PresenceChannel} clientEvents channel instance
     */
    getClientEventsChannel(channelName) {
        return this.connection.getChannel(channelName, PresenceChannel);
    }
}

export {
    Pushkin,
    ChannelSubscriptionError,
    WSConnectionError,
    ParamsValidationError
};
